import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { patientsService, Patient, getPatientActivityStatus } from '../services/patientsService';
import HeaderBar from '../components/HeaderBar';
import RoleMetrics from '../components/metrics/RoleMetrics';
import RecentInsights from '../components/insights/RecentInsights';
import { useIsMobile } from '../hooks/useIsMobile';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [totalPatients, setTotalPatients] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        setLoading(true);
        const response = await patientsService.getPatients(1);
        setPatients(response.results);
        setTotalPatients(response.count);
      } catch (error) {
        console.error('Failed to fetch patients:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPatients();
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 20) return 'Buenas tardes';
    return 'Buenas noches';
  };

  const getTodayDate = () => {
    const today = new Date();
    const options: Intl.DateTimeFormatOptions = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return today.toLocaleDateString('es-ES', options);
  };

  const getFullName = (patient: Patient) => {
    return `${patient.firstName} ${patient.lastName}`.trim() || patient.username;
  };

  const recentPatients = [...patients].slice(0, 5);


  return (
    <div style={{ padding: 0 }}>
      <HeaderBar title="Dashboard" />
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px 40px' }}>
        {/* Greeting Section */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 32, fontWeight: 700, color: '#1f2937', marginBottom: 8, marginTop: 0 }}>
            {getGreeting()}{user?.firstName ? `, Dr. ${user.lastName}` : ''}
          </h1>
          <p style={{ fontSize: 16, color: '#6b7280', margin: 0, textTransform: 'capitalize' }}>
            {getTodayDate()}
          </p>
        </div>

        {/* Role-based Metrics */}
        <RoleMetrics totalPatients={totalPatients} />

        {/* Quick Actions */}
        <div
          style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: 32,
          }}
        >
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1f2937', marginTop: 0, marginBottom: 20 }}>
            Acciones Rápidas
          </h2>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/app/patients?create=true')}
              style={{
                background: '#38b2ac',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '12px 20px',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: '0 2px 4px rgba(56, 178, 172, 0.2)',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#319795';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(56, 178, 172, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#38b2ac';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(56, 178, 172, 0.2)';
              }}
            >
              <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
              <span>Nuevo Paciente</span>
            </button>
            <button
              onClick={() => navigate('/app/patients')}
              style={{
                background: 'transparent',
                color: '#38b2ac',
                border: '1px solid #38b2ac',
                borderRadius: 8,
                padding: '12px 20px',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f0fdfa';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Ver Todos los Pacientes
            </button>
          </div>
        </div>

        {/* Recent Insights */}
        <RecentInsights />

        {/* Recent Patients */}
        <div
          style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1f2937', marginTop: 0, marginBottom: 20 }}>
            Pacientes Recientes
          </h2>
          {loading ? (
            <div style={{ padding: 40, fontSize: 16, color: '#6b7280', textAlign: 'center' }}>
              Cargando pacientes...
            </div>
          ) : recentPatients.length === 0 ? (
            <div style={{ padding: 40, fontSize: 16, color: '#6b7280', textAlign: 'center' }}>
              No hay pacientes registrados
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {recentPatients.map((patient) => (
                <div
                  key={patient.id}
                  onClick={() => navigate(`/app/patients/${patient.id}`)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    padding: 16,
                    borderRadius: 8,
                    border: '1px solid #f3f4f6',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f9fafb';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = '#f3f4f6';
                  }}
                >
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: patient.avatarUrl ? `url(${patient.avatarUrl})` : '#38b2ac',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 18,
                      fontWeight: 600,
                      color: '#fff',
                      flexShrink: 0,
                    }}
                  >
                    {!patient.avatarUrl && getFullName(patient).charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        color: '#1f2937',
                        fontSize: 16,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {getFullName(patient)}
                    </div>
                    {!isMobile && (
                      <div
                        style={{
                          color: '#6b7280',
                          fontSize: 14,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {patient.email}
                      </div>
                    )}
                  </div>
                  {!isMobile && (() => {
                    const status = getPatientActivityStatus(patient);
                    if (status === 'never') return (
                      <span style={{
                        display: 'inline-block',
                        background: '#fee2e2',
                        color: '#991b1b',
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '4px 10px',
                        borderRadius: 12,
                        whiteSpace: 'nowrap',
                      }}>
                        Nunca accedió
                      </span>
                    );
                    if (status === 'inactive') return (
                      <span style={{
                        display: 'inline-block',
                        background: '#fef3c7',
                        color: '#92400e',
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '4px 10px',
                        borderRadius: 12,
                        whiteSpace: 'nowrap',
                      }}>
                        Sin actividad +7 días
                      </span>
                    );
                    return null;
                  })()}
                  <div style={{ color: '#6b7280', fontSize: 24 }}>→</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
